import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EmptyStateWithNextAction } from '@cedarreachai/ui';
import { AsyncSection } from '@/components/AsyncSection';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CreateWorkspaceForm } from './CreateWorkspaceForm';
import { useSession, useWorkspaceList, type Session } from './useWorkspace';

export interface WorkspacePickerProps {
  /**
   * When the caller belongs to exactly one workspace, open it instead of
   * asking. The entry route sets this; the explicit "switch workspace" page
   * does not, so a person can still reach the create form.
   */
  readonly autoOpenSingle?: boolean;
}

/**
 * Which workspace to open (CLAUDE.md §1.4: tenancy is chosen, never inferred).
 *
 * Built from `GET /me` (roles) and `GET /workspaces` (the list). With none,
 * the only next action is to create one.
 */
export function WorkspacePickerScreen({ autoOpenSingle = false }: WorkspacePickerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const session = useSession();
  const workspaces = useWorkspaceList();

  const open = (id: string) => `/w/${id}/portfolio`;

  return (
    <main id="main-content" tabIndex={-1} className="mx-auto flex max-w-3xl flex-col gap-6 p-6">
      <header className="flex flex-col gap-1 border-b border-border pb-4">
        <h1 className="text-xl font-semibold tracking-tight">{t('workspaces.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('workspaces.subtitle')}</p>
      </header>

      <AsyncSection
        label={t('workspaces.title')}
        isPending={workspaces.isPending || session.isPending}
        error={workspaces.error ?? session.error}
        data={
          workspaces.data && session.data ? { page: workspaces.data, me: session.data } : undefined
        }
        onRetry={() => {
          void workspaces.refetch();
          void session.refetch();
        }}
        skeletonRows={2}
      >
        {({ page, me }) => {
          const only = page.items.length === 1 ? page.items[0] : undefined;
          if (autoOpenSingle && only) return <Navigate to={open(only.id)} replace />;
          return (
            <div className="flex flex-col gap-6">
              {page.items.length === 0 ? (
                <EmptyStateWithNextAction
                  title={t('errors.noWorkspaceTitle')}
                  description={t('workspaces.noneBody')}
                />
              ) : (
                <section aria-labelledby="workspace-list-heading" className="flex flex-col gap-2">
                  <h2 id="workspace-list-heading" className="text-base font-semibold">
                    {t('workspaces.yours')}
                  </h2>
                  <ul className="flex flex-col gap-2" data-testid="workspace-list">
                    {page.items.map((ws) => (
                      <li key={ws.id}>
                        <Link
                          to={open(ws.id)}
                          className="flex flex-col gap-0.5 rounded-lg border border-border p-3 hover:bg-accent"
                        >
                          <span className="font-medium">{ws.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {t('workspaces.row', {
                              role: roleLabel(t, me, ws.id),
                              locale: ws.default_locale,
                              timezone: ws.timezone,
                            })}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              <Card>
                <CardHeader>
                  <CardTitle>{t('workspaces.create.heading')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CreateWorkspaceForm onCreated={(ws) => navigate(open(ws.id))} />
                </CardContent>
              </Card>
            </div>
          );
        }}
      </AsyncSection>
    </main>
  );
}

function roleLabel(t: (key: string) => string, me: Session, workspaceId: string): string {
  const membership = me.memberships.find((m) => m.workspace_id === workspaceId);
  return membership ? t(`settings.members.roles.${membership.role}`) : t('workspaces.noRole');
}
